import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Icon } from '@iconify/react';
import SEO from '../../components/SEO';
import { signInWithRedirect, signOut, fetchAuthSession } from 'aws-amplify/auth';
import { jwtDecode } from 'jwt-decode';
import { fetchMemeCaseTitles } from '../../services/airtableService';
import PrivacyPolicy from '../../pages/PrivacyPolicy';
import Terms from '../../pages/Terms';
import Footer from '../../components/Footer';
import './Photo.css';
import '../styles/splash.css';
import '../styles/payment.css';
import '../styles/mypage.css';
import '../styles/deleteConfirm.css';
import '../styles/sample.css';
import '../styles/loading.css';

import { loadPhotoUserProfile, getIdTokenClaims, buildUserPayload } from '../src/auth/photoAuth';
import {
  NICEPAY_CLIENT_ID,
  generateOrderId,
  buildPhotoPaymentReturnUrl,
  createNicePayPaymentStartHtml,
  openNicePayPopupFromHtml,
} from '../src/payment/nicepay';

import PhotoSplash from '../components/PhotoSplash';
import PhotoPaymentPopup from '../components/PhotoPaymentPopup';
import PhotoMyPageModal from '../components/PhotoMyPageModal';
import PhotoOverlayModal from '../components/PhotoOverlayModal';
import PhotoDeleteConfirmPopup from '../components/PhotoDeleteConfirmPopup';
import PhotoLoginPopup from '../components/PhotoLoginPopup';
import PhotoLoadingPopup from '../components/PhotoLoadingPopup';
import PhotoSamplePopup from '../components/PhotoSamplePopup';
import PhotoGeneratedImagePopup from '../components/PhotoGeneratedImagePopup';

import PhotoUploadSection from '../components/main/PhotoUploadSection';
import PhotoOccasionSection from '../components/main/PhotoOccasionSection';
import PhotoMemeSection from '../components/main/PhotoMemeSection';
import PhotoTextSection from '../components/main/PhotoTextSection';
import PhotoGenerateSection from '../components/main/PhotoGenerateSection';

import { PHOTO_CONFIG } from '../src/constants/photoConfig';
import {
  fetchUserCoins,
  fetchImageHistory,
  notifyDeleteImageToWebhook,
  submitCustomerSupport as submitCustomerSupportWebhook,
} from '../src/services/photoWebhooks';
import { filterDeletedImages } from '../src/utils/history';
import { extractS3ObjectKeyFromUrl } from '../src/utils/s3';

function dataUrlToFile(dataUrl, filename, mimeType) {
  return fetch(dataUrl)
    .then((res) => res.blob())
    .then((blob) => new File([blob], filename || 'upload.png', { type: mimeType || blob.type || 'image/png' }));
}

const Photo = () => {
  const KAKAO_COGNITO_PROVIDER_NAME = process.env.REACT_APP_COGNITO_KAKAO_PROVIDER || 'Kakao';
  const [showSplash, setShowSplash] = useState(true);
  const reopenMyPagePendingRef = useRef(false);
  const [showPrivacyOverlay, setShowPrivacyOverlay] = useState(false);
  const [showTermsOverlay, setShowTermsOverlay] = useState(false);
  const [showCsOverlay, setShowCsOverlay] = useState(false);
  const [csTitle, setCsTitle] = useState('');
  const [csContent, setCsContent] = useState('');
  const [isCsSending, setIsCsSending] = useState(false);
  const [csError, setCsError] = useState(null);
  const [selectedOccasion, setSelectedOccasion] = useState('');
  const [selectedMeme, setSelectedMeme] = useState('');
  const [customText, setCustomText] = useState('');
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showLoadingPopup, setShowLoadingPopup] = useState(false);
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const [loadingImageIndex, setLoadingImageIndex] = useState(0);
  const [showSamplePopup, setShowSamplePopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const paymentWindowRef = useRef(null); // 결제 팝업 창 참조
  const [showLoginPopup, setShowLoginPopup] = useState(false); // 로그인 팝업 표시 여부
  const [selectedCharacterStyle, setSelectedCharacterStyle] = useState('real'); // 기본값: 리얼 사진 스타일
  const [userCoins, setUserCoins] = useState(0); // 보유 코인 (임시로 0으로 설정, 추후 API 연동)
  const [user, setUser] = useState(null); // 로그인한 사용자 정보
  const [userName, setUserName] = useState(''); // 사용자 이름/이메일
  const [showMyPage, setShowMyPage] = useState(false); // 마이페이지 모달 표시 여부
  const [imageHistory, setImageHistory] = useState([]); // 생성한 이미지 히스토리
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);
  // 삭제된 이미지 ID 배열 - localStorage에서 불러오기
  const [deletedImageIds, setDeletedImageIds] = useState(() => {
    try {
      const saved = localStorage.getItem('photo_deleted_image_ids');
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      return [];
    }
  });
  const [deleteConfirmPopup, setDeleteConfirmPopup] = useState({ show: false, imageId: null }); // 삭제 확인 팝업
  // 컴포넌트 마운트 시마다 항상 기본값으로 초기화 (캐시 방지)
  const [memeCaseLabels, setMemeCaseLabels] = useState(() => [
    { title: '밈 케이스 1', field: 'title1' },
    { title: '밈 케이스 2', field: 'title2' },
    { title: '밈 케이스 3', field: 'title3' }
  ]); // 동적 밈 케이스 라벨 (타이틀과 필드명 포함)
  const [memeTitlesLoaded, setMemeTitlesLoaded] = useState(false); // 웹훅 호출 완료 상태
  const [isGuest, setIsGuest] = useState(false); // 게스트 모드 여부
  const fileInputRef = useRef(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState(null);

  const refreshUserCoins = async (reason = 'manual') => {
    try {
      const coins = await fetchUserCoins({ reason, user });
      if (coins !== null) setUserCoins(coins);
    } catch (e) {
      // coin 조회 실패는 조용히 무시 (UX)
      // console.error('coin 조회 실패:', e);
    }
  };

  // 페이지 새로고침/재진입 시에도 user_info 웹훅을 호출해서 코인 동기화
  // 로딩 팝업 텍스트/이미지 순환 애니메이션
  useEffect(() => {
    if (!showLoadingPopup) {
      setLoadingTextIndex(0);
      setLoadingImageIndex(0);
      return;
    }

    const textInterval = setInterval(() => {
      setLoadingTextIndex((prev) => (prev + 1) % 3);
    }, 2000);

    const imageInterval = setInterval(() => {
      setLoadingImageIndex((prev) => (prev + 1) % 3);
    }, 2000);

    return () => {
      clearInterval(textInterval);
      clearInterval(imageInterval);
    };
  }, [showLoadingPopup]);

  useEffect(() => {
    refreshUserCoins('page_load');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // photo-page에서 스크롤바 숨기기
  useEffect(() => {
    document.body.classList.add('photo-page-scroll');
    document.documentElement.classList.add('photo-page-scroll');
    
    return () => {
      document.body.classList.remove('photo-page-scroll');
      document.documentElement.classList.remove('photo-page-scroll');
    };
  }, []);

  // 결제 팝업(나이스페이) 완료를 postMessage로 받으면 현재 페이지를 유지한 채로 UI만 갱신
  useEffect(() => {
    const allowedOrigins = new Set([
      window.location.origin, // localhost 또는 flowto-ai.com
      'https://wfw8chmuu9.execute-api.ap-northeast-2.amazonaws.com',
    ]);

    const onMessage = (event) => {
      if (!allowedOrigins.has(event.origin)) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'NICEPAY_PAYMENT_SUCCESS') {
        // 결제 성공: 결제 팝업 창 닫기 및 UI 팝업 닫기
        if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
          paymentWindowRef.current.close();
        }
        paymentWindowRef.current = null;
        setShowPaymentPopup(false);
        // 결제 성공 후 coin 재조회
        refreshUserCoins('payment_success');
        try {
          localStorage.setItem(
            'app_toast',
            JSON.stringify({ type: 'success', message: '결제가 완료되었습니다.', ts: Date.now() })
          );
          window.dispatchEvent(new Event('app_toast'));
        } catch (e) {
          // ignore
        }
      }
      if (data.type === 'NICEPAY_PAYMENT_FAIL') {
        // 결제 실패: 결제 팝업 창 닫기 및 UI 팝업 닫기
        if (paymentWindowRef.current && !paymentWindowRef.current.closed) {
          paymentWindowRef.current.close();
        }
        paymentWindowRef.current = null;
        setShowPaymentPopup(false);
        refreshUserCoins('payment_fail');
        try {
          localStorage.setItem(
            'app_toast',
            JSON.stringify({ type: 'error', message: data.message || '결제에 실패했습니다.', ts: Date.now() })
          );
          window.dispatchEvent(new Event('app_toast'));
        } catch (e) {
          // ignore
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 사용자 정보 가져오기
  useEffect(() => {
    const getUserAndAttributes = async () => {
      const tryLoad = async (attempt = 0) => {
        try {
          // 소셜 로그인 직후에는 code->token 교환/세션 반영 타이밍 때문에
          // getCurrentUser가 간헐적으로 실패할 수 있어 먼저 세션을 한 번 확정한다.
          try {
            await fetchAuthSession();
          } catch (e) {
            // ignore (비로그인 상태일 수도 있음)
          }

          const { user: loadedUser, displayName } = await loadPhotoUserProfile();
          setUser(loadedUser);
          setUserName(displayName);

          // 로그인 pending 플래그 정리
          try {
            sessionStorage.removeItem('photo_login_pending');
            sessionStorage.removeItem('photo_login_provider');
            sessionStorage.removeItem('photo_login_started_at');
          } catch (e) {
            // ignore
          }

          // 로그인 성공 시 게스트 모드 해제
          setIsGuest(false);

          // 로그인 상태라면 splash 자동 종료
          setShowSplash(false);

          // 로그인 직후 coin 초기 로드
          // (setUser 직후이므로 refreshUserCoins 내부에서 user 체크에 걸릴 수 있어 지연 후 호출)
          setTimeout(() => {
            refreshUserCoins('login');
          }, 0);

          // 로그인 후 원래 위치로 리다이렉트
          const redirect = localStorage.getItem('login_redirect');
          if (redirect && window.location.pathname !== redirect) {
            localStorage.removeItem('login_redirect');
            window.location.replace(redirect);
          }
        } catch (e) {
          // 소셜 로그인 직후 1회 재시도 (특히 OIDC 커스텀 IdP에서 간헐적)
          if (attempt < 1) {
            await new Promise((r) => setTimeout(r, 250));
            return tryLoad(attempt + 1);
          }

          // 방금 소셜 로그인을 시도했던 경우, 실패를 사용자에게 명확히 알린다.
          try {
            const pending = sessionStorage.getItem('photo_login_pending');
            const providerName = sessionStorage.getItem('photo_login_provider') || '소셜';
            if (pending === '1') {
              let sessionHint = '';
              try {
                const s = await fetchAuthSession();
                const hasIdToken = Boolean(s?.tokens?.idToken);
                sessionHint = hasIdToken ? ' (세션 토큰 있음)' : ' (세션 토큰 없음)';
              } catch (e3) {
                sessionHint = ' (세션 조회 실패)';
              }

              const errMsg = e?.message ? ` / ${String(e.message)}` : '';
              sessionStorage.removeItem('photo_login_pending');
              sessionStorage.removeItem('photo_login_provider');
              sessionStorage.removeItem('photo_login_started_at');
              localStorage.setItem(
                'app_toast',
                JSON.stringify({
                  type: 'error',
                  message: `${providerName} 로그인은 완료됐지만 사용자 정보를 가져오지 못했습니다${sessionHint}. (Cognito에서 email 필수/IdP attribute mapping/email scope를 확인해주세요)${errMsg}`,
                  ts: Date.now(),
                })
              );
              window.dispatchEvent(new Event('app_toast'));
            }
          } catch (e2) {
            // ignore
          }

          // 비로그인(에러)이면 아무것도 하지 않음
          setUser(null);
          setUserName('');
        }
      };

      await tryLoad(0);
    };
    getUserAndAttributes();
    
    // 결제 직전 상태 복원 (sessionStorage)
    const savedDraft = sessionStorage.getItem('photo_draft_state');
    if (savedDraft) {
      try {
        const draft = JSON.parse(savedDraft);
        if (draft?.selectedOccasion) setSelectedOccasion(draft.selectedOccasion);
        if (draft?.selectedMeme) setSelectedMeme(draft.selectedMeme);
        if (typeof draft?.customText === 'string') setCustomText(draft.customText);
        if (draft?.selectedCharacterStyle) setSelectedCharacterStyle(draft.selectedCharacterStyle);
        if (draft?.imagePreview) {
          setImagePreview(draft.imagePreview);
          // 업로드 파일도 복원 (가능한 경우)
          dataUrlToFile(draft.imagePreview, draft.uploadedImageName, draft.uploadedImageType)
            .then((file) => {
              setUploadedImage(file);
            })
            .catch(() => {
              // ignore
            });
        }
        // 복원은 1회성으로 사용 (반복 복원 방지)
        sessionStorage.removeItem('photo_draft_state');
      } catch (e) {
        // ignore
      }
    }

    // localStorage에서 이미지 히스토리 불러오기
    const savedHistory = localStorage.getItem('photo_image_history');
    if (savedHistory) {
      try {
        const parsed = JSON.parse(savedHistory);
        const now = Date.now();
        const maxAgeMs = PHOTO_CONFIG.historyRetentionDays * 24 * 60 * 60 * 1000;
        const filtered = Array.isArray(parsed)
          ? parsed.filter((item) => {
              const t = item?.createdAt ? Date.parse(item.createdAt) : NaN;
              if (!Number.isFinite(t)) return false;
              return now - t <= maxAgeMs;
            })
          : [];
        // 삭제된 이미지 필터링
        const filteredWithoutDeleted = filterDeletedImages(filtered, deletedImageIds);
        setImageHistory(filteredWithoutDeleted);
        // 저장소도 7일 이상 지난 건 정리(용량/UX) 및 삭제된 이미지 제외
        localStorage.setItem('photo_image_history', JSON.stringify(filteredWithoutDeleted));
      } catch (e) {
        console.error('히스토리 로드 실패:', e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 스플래시 화면 시작 시 n8n 웹훅에서 밈 케이스 타이틀 가져오기
  useEffect(() => {
    // 캐시 방지를 위해 컴포넌트 마운트 시 강제로 기본값으로 초기화
    setMemeCaseLabels([
      { title: '밈 케이스 1', field: 'title1' },
      { title: '밈 케이스 2', field: 'title2' },
      { title: '밈 케이스 3', field: 'title3' }
    ]);
    setMemeTitlesLoaded(false);
    
    // 약간의 지연을 두어 state 초기화가 확실히 반영되도록 함
    const fetchMemeTitles = async () => {
      // state 초기화가 확실히 반영되도록 약간의 지연
      await new Promise(resolve => setTimeout(resolve, 0));
      
      try {
        const titles = await fetchMemeCaseTitles();
        if (titles && titles.length >= 3) {
          // title1, title2, title3을 모두 가져와서 표시
          setMemeCaseLabels(titles);
          console.log('밈 케이스 타이틀 업데이트:', titles);
        } else {
          console.warn('밈 케이스 타이틀을 가져오지 못했습니다. 기본값 사용.');
          setMemeCaseLabels([
            { title: '밈 케이스 1', field: 'title1' },
            { title: '밈 케이스 2', field: 'title2' },
            { title: '밈 케이스 3', field: 'title3' }
          ]);
        }
        // 웹훅 호출 완료 (성공 또는 실패 모두)
        setMemeTitlesLoaded(true);
      } catch (error) {
        console.error('밈 케이스 타이틀 가져오기 실패:', error);
        // 에러 발생 시 기본값으로 명시적으로 설정
        setMemeCaseLabels([
          { title: '밈 케이스 1', field: 'title1' },
          { title: '밈 케이스 2', field: 'title2' },
          { title: '밈 케이스 3', field: 'title3' }
        ]);
        setMemeTitlesLoaded(true);
      }
    };
    
    fetchMemeTitles();
    
    // 컴포넌트 언마운트 시 초기화 (선택사항)
    return () => {
      setMemeCaseLabels([
        { title: '밈 케이스 1', field: 'title1' },
        { title: '밈 케이스 2', field: 'title2' },
        { title: '밈 케이스 3', field: 'title3' }
      ]);
      setMemeTitlesLoaded(false);
    };
  }, []); // 컴포넌트 마운트 시 한 번만 호출

  const refreshImageHistoryFromWebhook = async (reason = 'mypage_open') => {
    if (!user) return;
    setIsHistoryLoading(true);
    setHistoryError(null);

    // 최신 삭제된 이미지 ID 목록 가져오기 (localStorage에서 직접 읽어서 최신 상태 보장)
    let currentDeletedIds = deletedImageIds;
    try {
      const saved = localStorage.getItem('photo_deleted_image_ids');
      if (saved) {
        currentDeletedIds = JSON.parse(saved);
      }
    } catch (e) {
      console.error('삭제된 이미지 ID 로드 실패:', e);
    }

    try {
      const items = await fetchImageHistory({
        reason,
        user,
        deletedIds: currentDeletedIds,
        retentionDays: PHOTO_CONFIG.historyRetentionDays,
        limit: 50,
      });

      setImageHistory(items);
      try {
        localStorage.setItem('photo_image_history', JSON.stringify(items));
      } catch (e) {}
    } catch (e) {
      console.error('이미지 히스토리 웹훅 로드 실패:', e);
      setHistoryError('이미지 히스토리를 불러오지 못했습니다.');
    } finally {
      setIsHistoryLoading(false);
    }
  };

  const openPrivacyOverlay = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('terms');
      url.searchParams.set('privacy', '1');
      window.history.pushState({ photoPrivacy: true }, '', url.toString());
    } catch (e) {
      // ignore
    }
    setShowPrivacyOverlay(true);
  };

  const closePrivacyOverlay = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('privacy');
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      // ignore
    }
    setShowPrivacyOverlay(false);
    // 개인정보 방침에서 돌아오면 마이페이지는 그대로 보이게 유지
    setShowMyPage(true);
  };

  const openTermsOverlay = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('privacy');
      url.searchParams.set('terms', '1');
      window.history.pushState({ photoTerms: true }, '', url.toString());
    } catch (e) {
      // ignore
    }
    setShowTermsOverlay(true);
  };

  const closeTermsOverlay = () => {
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete('terms');
      window.history.replaceState({}, '', url.toString());
    } catch (e) {
      // ignore
    }
    setShowTermsOverlay(false);
    setShowMyPage(true);
  };

  const openCsOverlay = () => {
    setCsError(null);
    setShowCsOverlay(true);
  };

  const closeCsOverlay = () => {
    setShowCsOverlay(false);
    setCsError(null);
    setIsCsSending(false);
    // 닫을 때는 내용은 유지 (실수 방지). 필요하면 여기서 초기화할 수 있음.
    setShowMyPage(true);
  };

  const handleSubmitCustomerSupport = async () => {
    const title = String(csTitle || '').trim();
    const content = String(csContent || '').trim();
    if (!title || !content) {
      setCsError('제목과 내용을 모두 입력해주세요.');
      return;
    }
    if (isCsSending) return;

    setIsCsSending(true);
    setCsError(null);
    try {
      await submitCustomerSupportWebhook({ title, content, isGuest, user, userName });

      try {
        localStorage.setItem(
          'app_toast',
          JSON.stringify({ type: 'success', message: '문의가 전송되었습니다.', ts: Date.now() })
        );
        window.dispatchEvent(new Event('app_toast'));
      } catch (e) {
        // ignore
      }

      // 성공 시 입력 초기화 + 닫기
      setCsTitle('');
      setCsContent('');
      setShowCsOverlay(false);
      setShowMyPage(true);
    } catch (e) {
      console.error('고객센터 문의 전송 실패:', e);
      setCsError('문의 전송에 실패했습니다. 잠시 후 다시 시도해주세요.');
      try {
        localStorage.setItem(
          'app_toast',
          JSON.stringify({ type: 'error', message: '문의 전송에 실패했습니다.', ts: Date.now() })
        );
        window.dispatchEvent(new Event('app_toast'));
      } catch (e2) {
        // ignore
      }
    } finally {
      setIsCsSending(false);
    }
  };

  // 개인정보 사용 방침 페이지에서 "뒤로가기"로 돌아왔을 때 마이페이지 모달을 복원
  useEffect(() => {
    try {
      const v = sessionStorage.getItem('photo_reopen_mypage');
      if (v === '1') {
        sessionStorage.removeItem('photo_reopen_mypage');
        reopenMyPagePendingRef.current = true;
      }

      // /privacy-policy 이동 전에 스플래시/게스트 상태를 저장해두었다면 복원
      const skipSplash = sessionStorage.getItem('photo_skip_splash');
      if (skipSplash === '1') {
        sessionStorage.removeItem('photo_skip_splash');
        setShowSplash(false);
      }

      const guest = sessionStorage.getItem('photo_restore_guest');
      if (guest === '1') {
        sessionStorage.removeItem('photo_restore_guest');
        setIsGuest(true);
      }

      // 쿼리로 직접 들어온 경우(/photo?privacy=1)에도 오버레이 표시
      try {
        const qs = new URLSearchParams(window.location.search);
        if (qs.get('privacy') === '1') {
          setShowPrivacyOverlay(true);
          // 마이페이지를 함께 보여주고 싶으면 true 유지
          setShowMyPage(true);
        } else if (qs.get('terms') === '1') {
          setShowTermsOverlay(true);
          setShowMyPage(true);
        }
      } catch (e2) {
        // ignore
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!reopenMyPagePendingRef.current) return;
    // 스플래시(또는 초기 데이터 로딩)가 끝난 뒤에 모달을 열어야 사용자에게 보임
    if (showSplash || !memeTitlesLoaded) return;
    setShowMyPage(true);
    reopenMyPagePendingRef.current = false;
  }, [showSplash, memeTitlesLoaded]);

  // 뒤로가기(popstate)로 privacy=1이 사라지면 오버레이만 닫고 마이페이지는 유지
  useEffect(() => {
    const onPopState = () => {
      try {
        const qs = new URLSearchParams(window.location.search);
        const isPrivacy = qs.get('privacy') === '1';
        const isTerms = qs.get('terms') === '1';

        if (!isPrivacy && showPrivacyOverlay) {
          setShowPrivacyOverlay(false);
          setShowMyPage(true);
        }
        if (!isTerms && showTermsOverlay) {
          setShowTermsOverlay(false);
          setShowMyPage(true);
        }
      } catch (e) {
        // ignore
      }
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [showPrivacyOverlay, showTermsOverlay]);

  // 마이페이지 열릴 때마다(로그인 상태) 서버 히스토리 새로 불러오기
  useEffect(() => {
    if (!showMyPage) return;
    if (!user) return;
    refreshImageHistoryFromWebhook('mypage_open');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showMyPage, user]);

  // 스플래시 화면은 로그인 버튼을 눌러야만 메인 화면으로 전환됨

  // 이미지 업로드 핸들러
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // 이미지 제거
  const handleRemoveImage = () => {
    setUploadedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 이미지 삭제 확인 팝업 표시
  const handleDeleteImage = (imageId) => {
    setDeleteConfirmPopup({ show: true, imageId });
  };

  // 이미지 삭제 확인
  const confirmDeleteImage = () => {
    if (deleteConfirmPopup.imageId) {
      const deletedId = deleteConfirmPopup.imageId;

      // 삭제 이벤트를 n8n 웹훅으로 전송 (S3 삭제는 n8n에서 수행)
      // UI 반응성을 위해 await 하지 않음
      notifyDeleteImageToWebhook({ deletedId, imageHistory, user }).catch((e) => {
        console.warn('[photo/delete-image] webhook notify failed:', e);
      });

      const newDeletedIds = [...deletedImageIds, deletedId];
      setDeletedImageIds(newDeletedIds);
      // localStorage에 저장하여 영구적으로 유지
      try {
        localStorage.setItem('photo_deleted_image_ids', JSON.stringify(newDeletedIds));
      } catch (e) {
        console.error('삭제된 이미지 ID 저장 실패:', e);
      }
      // imageHistory state에서도 즉시 제거 및 localStorage 업데이트
      setImageHistory(prevHistory => {
        const filteredHistory = filterDeletedImages(prevHistory, newDeletedIds);
        // localStorage의 히스토리도 업데이트
        try {
          localStorage.setItem('photo_image_history', JSON.stringify(filteredHistory));
        } catch (e) {
          console.error('히스토리 업데이트 실패:', e);
        }
        return filteredHistory;
      });
      setDeleteConfirmPopup({ show: false, imageId: null });
    }
  };

  // 이미지 삭제 취소
  const cancelDeleteImage = () => {
    setDeleteConfirmPopup({ show: false, imageId: null });
  };

  // 삭제된 이미지 필터링 함수
  // (moved) filterDeletedImages -> `src/photo/src/utils/history.js`

  // 실제 이미지 생성 로직 (결제/코인 조건과 분리)
  const doGenerateImage = async () => {
    setIsGenerating(true);
    setShowLoadingPopup(true);

    try {
      // user payload (idToken 기반) - n8n photo-generator로 함께 전달
      let idToken = null;
      let claims = {};
      try {
        const session = await fetchAuthSession();
        idToken = session.tokens?.idToken?.toString() || null;
        if (idToken) claims = jwtDecode(idToken) || {};
      } catch (e) {
        idToken = null;
        claims = {};
      }
      const payload = {
        cognitoSub: claims?.sub,
        email: claims?.email,
        name: claims?.name,
        phone: claims?.phone_number,
        userName: user?.username || claims?.['cognito:username'] || claims?.username,
        eventTime: new Date().toISOString(),
      };

      const formData = new FormData();
      formData.append('occasion', selectedOccasion);
      formData.append('meme', selectedMeme);
      formData.append('customText', customText);
      formData.append('characterStyle', selectedCharacterStyle || 'real');
      formData.append('image', uploadedImage);
      formData.append('payload', JSON.stringify(payload));

      const response = await fetch('https://bshong-n8n.ngrok.io/webhook/photo-generator', {
        method: 'POST',
        headers: {
          ...(idToken ? { Authorization: `Bearer ${idToken}` } : {}),
          'ngrok-skip-browser-warning': '69420',
        },
        body: formData,
      });

      const contentType = response.headers.get('content-type');
      let finalImageUrl = null;

      if (response.ok && contentType && contentType.startsWith('image/')) {
        // 응답이 실제 이미지(binary) 파일
        const imgBlob = await response.blob();
        finalImageUrl = URL.createObjectURL(imgBlob);
        setGeneratedImageUrl(finalImageUrl);
      } else if (response.ok && contentType && contentType.includes('application/json')) {
        const data = await response.json();
        if (data.imageUrl) {
          finalImageUrl = data.imageUrl;
          setGeneratedImageUrl(finalImageUrl);
        } else if (data.imageBase64) {
          finalImageUrl = data.imageBase64;
          setGeneratedImageUrl(finalImageUrl);
        } else {
          alert('이미지 응답을 찾을 수 없습니다.');
          return;
        }
      } else {
        alert('이미지 생성 실패 (서버 오류)');
        return;
      }

      // 이미지 히스토리에 추가
      if (finalImageUrl) {
        const s3KeyFromFinalUrl = extractS3ObjectKeyFromUrl(finalImageUrl);
        const newHistoryItem = {
          id: Date.now(),
          url: finalImageUrl,
          occasion: selectedOccasion,
          meme: selectedMeme,
          customText: customText,
          characterStyle: selectedCharacterStyle || 'real',
          createdAt: new Date().toISOString(),
          ...(s3KeyFromFinalUrl ? { s3Key: s3KeyFromFinalUrl } : {}),
        };
        const now = Date.now();
        const maxAgeMs = PHOTO_CONFIG.historyRetentionDays * 24 * 60 * 60 * 1000;
        const pruned = Array.isArray(imageHistory)
          ? imageHistory.filter((item) => {
              const t = item?.createdAt ? Date.parse(item.createdAt) : NaN;
              if (!Number.isFinite(t)) return false;
              return now - t <= maxAgeMs;
            })
          : [];
        const updatedHistory = [newHistoryItem, ...pruned].slice(0, 50); // 최대 50개 + 7일 내만 유지
        // 삭제된 이미지 필터링
        const filteredHistory = filterDeletedImages(updatedHistory, deletedImageIds);
        setImageHistory(filteredHistory);
        localStorage.setItem('photo_image_history', JSON.stringify(filteredHistory));

        // 생성 성공 후 coin 정보 새로고침
        try {
          await refreshUserCoins('generate_complete');
        } catch (e) {
          // ignore
        }
      }
    } catch (err) {
      alert('네트워크 오류 혹은 알 수 없는 오류');
      setShowLoadingPopup(false);
    } finally {
      setIsGenerating(false);
      setShowLoadingPopup(false);
    }
  };

  // 생성하기 핸들러
  const handleGenerate = async () => {
    if (!uploadedImage) {
      alert('이미지를 업로드해주세요.');
      return;
    }
    if (!selectedOccasion) {
      alert('기념일을 선택해주세요.');
      return;
    }
    if (!selectedMeme) {
      alert('최신 밈을 선택해주세요.');
      return;
    }

    // 게스트 모드이고 로그인하지 않은 경우 로그인 팝업 표시
    if (isGuest && !user) {
      setShowLoginPopup(true);
      return;
    }

    // 로그인한 경우: 스타일 선택/결제(또는 코인으로 생성) 팝업 표시
    // 팝업이 열릴 때 기본값 설정 (이미 설정되어 있으면 유지)
    if (!selectedCharacterStyle) {
      setSelectedCharacterStyle('real');
    }
    setShowPaymentPopup(true);
    return;
  };

  const handleGenerateWithCoins = async () => {
    const canGenerate = Number(userCoins) >= PHOTO_CONFIG.requiredCoins;
    if (!canGenerate) return;
    if (!selectedCharacterStyle) {
      alert('캐릭터 스타일을 선택해주세요.');
      return;
    }
    setShowPaymentPopup(false);
    await doGenerateImage();
  };

  // 카카오 페이 결제 핸들러
  const handleKakaoPay = async () => {
    if (!selectedCharacterStyle) {
      alert('캐릭터 스타일을 선택해주세요.');
      return;
    }

    if (!window.AUTHNICE) {
      alert('결제 연동 스크립트가 로드되지 않았습니다. 새로고침 후 다시 시도해주세요.');
      return;
    }

    // Photo 결제: 10 coin 구매 = 1,000원 (UI에 맞춤)
    const amount = 1000;
    const goodsName = '10 coin 구매';
    // 결제 승인(returnUrl) 시점에:
    // - redirect: 결제 직전 경로
    // - origin: 부모창(origin) (Lambda에서 postMessage 대상 제한에 사용)
    // 를 함께 전달
    const redirectPath = window.location.pathname || '/photo';
    const origin = window.location.origin;

    // Lambda에서 n8n으로 사용자 정보를 같은 payload 포맷으로 넘기기 위해 query로 전달
    // (주의: query에 포함되므로 민감정보 정책/로깅에 유의)
    const { claims } = await getIdTokenClaims();
    const payload = buildUserPayload({ user, claims });
    const returnUrl = buildPhotoPaymentReturnUrl({ redirectPath, origin, payload, popup: false });

    // 결제창 호출 전, 결제 후 돌아올 위치를 저장 (백엔드 승인 후 프론트 복귀 시 활용 가능)
    try {
      localStorage.setItem('payment_redirect', window.location.pathname || '/photo');
    } catch (e) {
      // ignore
    }

    // 결제 직전 페이지 상태 저장 (복귀 시 그대로 복원)
    try {
      const draft = {
        selectedOccasion,
        selectedMeme,
        customText,
        selectedCharacterStyle,
        imagePreview, // dataURL
        uploadedImageName: uploadedImage?.name,
        uploadedImageType: uploadedImage?.type,
      };
      sessionStorage.setItem('photo_draft_state', JSON.stringify(draft));
    } catch (e) {
      // ignore
    }

    // 결제 승인 URL에 팝업 플래그 추가
    const returnUrlWithPopup = buildPhotoPaymentReturnUrl({ redirectPath, origin, payload, popup: true });

    // 결제 페이지를 팝업 창에서 열기
    // AUTHNICE SDK는 현재 창에서만 작동하므로, 결제 시작을 위한 임시 페이지를 팝업으로 열고
    // 그 페이지에서 결제를 진행하도록 함
    //
    // NOTE: blob: URL 뒤에 '?a=b' 같은 쿼리를 붙이면 브라우저가 다른 리소스로 취급하여
    // ERR_FILE_NOT_FOUND가 날 수 있음. 따라서 파라미터는 HTML에 직접 주입하고 blob URL은 그대로 연다.
    const paymentConfig = {
      clientId: NICEPAY_CLIENT_ID,
      method: 'kakaopay',
      orderId: generateOrderId(),
      amount,
      goodsName,
      returnUrl: returnUrlWithPopup,
      origin,
    };

    const paymentStartHtml = createNicePayPaymentStartHtml(paymentConfig);

    const paymentWindow = openNicePayPopupFromHtml(paymentStartHtml, {
      name: 'payment',
      width: 500,
      height: 700,
    });

    if (!paymentWindow) {
      alert('팝업이 차단되었습니다. 팝업 차단을 해제한 후 다시 시도해주세요.');
      // 팝업이 차단된 경우 기존 방식으로 진행
      window.AUTHNICE.requestPay({
        clientId: NICEPAY_CLIENT_ID,
        method: 'kakaopay',
        orderId: generateOrderId(),
        amount,
        goodsName,
        returnUrl,
        fnError: function (result) {
          alert(result?.errorMsg || '결제 중 오류가 발생했습니다.');
        },
      });
      return;
    }

    paymentWindowRef.current = paymentWindow;

    // 팝업 창이 닫히는 것을 감지
    const checkClosed = setInterval(() => {
      if (paymentWindow.closed) {
        clearInterval(checkClosed);
        paymentWindowRef.current = null;
        // 팝업이 닫혔지만 postMessage를 받지 못한 경우 (사용자가 직접 닫은 경우 등)
        // 결제 팝업 UI는 유지
      }
    }, 500);
  };

  // 로그아웃 핸들러
  const handleLogout = async () => {
    try {
      await signOut();
      setUser(null);
      setUserName('');
      setShowMyPage(false);
      setShowSplash(true);
      window.location.reload();
    } catch (error) {
      console.error('로그아웃 오류:', error);
      alert('로그아웃 중 오류가 발생했습니다.');
    }
  };

  const redirectToSocialLogin = (provider) => {
    localStorage.setItem('login_redirect', window.location.pathname);
    try {
      const providerName = typeof provider === 'string' ? provider : provider?.custom || 'unknown';
      sessionStorage.setItem('photo_login_pending', '1');
      sessionStorage.setItem('photo_login_provider', String(providerName));
      sessionStorage.setItem('photo_login_started_at', String(Date.now()));
    } catch (e) {
      // ignore
    }
    signInWithRedirect({ provider });
  };

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'AI 이미지 생성 - FLOWTO.ai',
    description: 'nanobanana 3.0을 사용한 AI 이미지 생성 서비스',
    applicationCategory: 'MultimediaApplication',
    operatingSystem: 'Web',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'KRW'
    },
    provider: {
      '@type': 'Organization',
      name: 'FLOWTO.ai',
      url: 'https://flowto-ai.com'
    }
  };

  return (
    <div className="photo-page">
      <SEO
        title="AI 이미지 생성"
        description="nanobanana 3.0을 사용한 AI 이미지 생성 서비스"
        url="/photo"
        type="website"
        structuredData={structuredData}
      />
      <PhotoSplash
        showSplash={showSplash}
        memeTitlesLoaded={memeTitlesLoaded}
        onGoogleLogin={() => redirectToSocialLogin('Google')}
        onKakaoLogin={() => redirectToSocialLogin({ custom: KAKAO_COGNITO_PROVIDER_NAME })}
        onEnterGuest={() => {
          setIsGuest(true);
          setShowSplash(false);
        }}
      />

      
      {!showSplash && memeTitlesLoaded && (
        <motion.div
          className="photo-main"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="photo-container">
            <PhotoUploadSection
              isGuest={isGuest}
              user={user}
              userName={userName}
              onOpenMyPage={() => setShowMyPage(true)}
              onGoogleLogin={() => redirectToSocialLogin('Google')}
              onKakaoLogin={() => redirectToSocialLogin({ custom: KAKAO_COGNITO_PROVIDER_NAME })}
              imagePreview={imagePreview}
              onRemoveImage={handleRemoveImage}
              onPickFile={() => fileInputRef.current?.click()}
              fileInputRef={fileInputRef}
              onImageUpload={handleImageUpload}
            />

            <PhotoOccasionSection selectedOccasion={selectedOccasion} onSelect={setSelectedOccasion} />

            <PhotoMemeSection
              memeCaseLabels={memeCaseLabels}
              selectedMeme={selectedMeme}
              onSelectMeme={setSelectedMeme}
              onOpenSamplePopup={() => setShowSamplePopup(true)}
            />

            <PhotoTextSection
              customText={customText}
              onChangeText={(t) => setCustomText(String(t || '').slice(0, 10))}
              maxLength={10}
            />

            <PhotoGenerateSection
              onGenerate={handleGenerate}
              isGenerating={isGenerating}
              disabled={isGenerating || !uploadedImage || !selectedOccasion || !selectedMeme}
              subtitle="이미지 생성에 nanobanana 3.0을 사용합니다"
            />

            {/* Photo 페이지 전용 Footer */}
            <div className="photo-footer-wrapper">
              <Footer />
            </div>
          </div>
          {/* 생성된 이미지 표시 */}
          
          {/* 로딩 팝업 */}
          <PhotoLoadingPopup
            show={showLoadingPopup}
            zIndex={2400}
            loadingImageIndex={loadingImageIndex}
            loadingTextIndex={loadingTextIndex}
          />

          {/* 생성된 이미지 팝업 */}
          <PhotoGeneratedImagePopup
            url={generatedImageUrl}
            onClose={() => setGeneratedImageUrl(null)}
            zIndex={2500}
          />
        </motion.div>
      )}

      {/* 샘플 이미지 팝업 */}
      <PhotoSamplePopup show={showSamplePopup} onClose={() => setShowSamplePopup(false)} />

      {/* 결제 팝업 */}
      <PhotoPaymentPopup
        show={showPaymentPopup}
        onClose={() => setShowPaymentPopup(false)}
        userCoins={userCoins}
        requiredCoins={PHOTO_CONFIG.requiredCoins}
        selectedCharacterStyle={selectedCharacterStyle}
        setSelectedCharacterStyle={setSelectedCharacterStyle}
        isGenerating={isGenerating}
        onGenerateWithCoins={handleGenerateWithCoins}
        onKakaoPay={handleKakaoPay}
      />

      {/* 마이페이지 모달 */}
      <PhotoMyPageModal
        show={showMyPage}
        onClose={() => setShowMyPage(false)}
        userName={userName}
        userCoins={userCoins}
        isHistoryLoading={isHistoryLoading}
        historyError={historyError}
        imageHistory={imageHistory}
        deletedImageIds={deletedImageIds}
        onDeleteImage={handleDeleteImage}
        onSelectImage={(url) => setGeneratedImageUrl(url)}
        onOpenCs={openCsOverlay}
        onOpenPrivacy={openPrivacyOverlay}
        onOpenTerms={openTermsOverlay}
        onLogout={handleLogout}
      />

      {/* 개인정보 처리방침 오버레이 (/photo 내부) */}
      <PhotoOverlayModal
        show={showPrivacyOverlay}
        title="개인정보 처리방침"
        onClose={closePrivacyOverlay}
        zIndex={20010}
        modalStyle={{ width: 'min(980px, calc(100vw - 24px))', height: 'min(86vh, 860px)', overflow: 'hidden' }}
        contentStyle={{ padding: 0, height: 'calc(100% - 56px)', overflow: 'auto' }}
      >
        <div style={{ padding: 16 }}>
          <PrivacyPolicy />
        </div>
      </PhotoOverlayModal>

      {/* 이용약관 오버레이 (/photo 내부) */}
      <PhotoOverlayModal
        show={showTermsOverlay}
        title="이용약관"
        onClose={closeTermsOverlay}
        zIndex={20011}
        modalStyle={{ width: 'min(980px, calc(100vw - 24px))', height: 'min(86vh, 860px)', overflow: 'hidden' }}
        contentStyle={{ padding: 0, height: 'calc(100% - 56px)', overflow: 'auto' }}
      >
        <div style={{ padding: 16 }}>
          <Terms />
        </div>
      </PhotoOverlayModal>

      {/* 고객센터 문의 오버레이 (/photo 내부) */}
      <PhotoOverlayModal
        show={showCsOverlay}
        title="고객센터 문의"
        onClose={closeCsOverlay}
        zIndex={20012}
        modalStyle={{ width: 'min(720px, calc(100vw - 24px))', height: 'min(76vh, 720px)', overflow: 'hidden' }}
        contentStyle={{ height: 'calc(100% - 56px)' }}
      >
        <div className="photo-cs-form">
          <label className="photo-cs-label">
            <span className="photo-cs-label-text">제목</span>
            <input
              className="photo-cs-input"
              type="text"
              value={csTitle}
              onChange={(e) => setCsTitle(e.target.value)}
              placeholder="제목을 입력해주세요"
              maxLength={120}
              disabled={isCsSending}
            />
          </label>

          <label className="photo-cs-label">
            <span className="photo-cs-label-text">내용</span>
            <textarea
              className="photo-cs-textarea"
              value={csContent}
              onChange={(e) => setCsContent(e.target.value)}
              placeholder="내용을 입력해주세요"
              rows={10}
              disabled={isCsSending}
            />
          </label>

          {csError && <div className="photo-cs-error">{csError}</div>}

          <div className="photo-cs-actions">
            <button
              type="button"
              className="photo-cs-cancel-btn"
              onClick={closeCsOverlay}
              disabled={isCsSending}
            >
              취소
            </button>
            <button
              type="button"
              className="photo-cs-send-btn"
              onClick={handleSubmitCustomerSupport}
              disabled={isCsSending}
            >
              {isCsSending ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Icon icon="solar:refresh-bold" width={16} height={16} className="photo-spinner-icon" style={{ animation: 'spin 1s linear infinite' }} />
                  전송 중
                </span>
              ) : (
                '보내기'
              )}
            </button>
          </div>
        </div>
      </PhotoOverlayModal>

      {/* 로그인 팝업 */}
      <PhotoLoginPopup
        show={showLoginPopup}
        onClose={() => setShowLoginPopup(false)}
        onGoogleLogin={() => redirectToSocialLogin('Google')}
        onKakaoLogin={() => redirectToSocialLogin({ custom: KAKAO_COGNITO_PROVIDER_NAME })}
      />

      {/* 삭제 확인 팝업 */}
      <PhotoDeleteConfirmPopup
        show={deleteConfirmPopup.show}
        onCancel={cancelDeleteImage}
        onConfirm={confirmDeleteImage}
      />
    </div>
  );
};

export default Photo;
